<!-- MemberAccountForm.vue -->
<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";
import { toTitleCase } from "@/utils/stringUtils";
import api from "@/services/api";

// Store
import { useManagedAccountStore } from "@/stores/useManagedAccountStore";
import { useUserStore } from "@/stores/useUserStore";

// Delegação: só o membro com a permissão administra as contas do titular.
import { useMemberRestrictions } from "@/composables/useMemberRestrictions";

import EditMemberAccountForm from "./EditMemberAccountForm.vue";
import DeleteMemberAccountForm from "./DeleteMemberAccountForm.vue";
import BatchDeleteMemberAccountForm from "./BatchDeleteMemberAccountForm.vue";

// Modal de card de detalhes
import MemberAccountCardModal from "./MemberAccountCardModal.vue";

// Card do USUÁRIO por trás do vínculo, aberto pelo avatar. É outra coisa do que
// o MemberAccountCardModal acima: aquele mostra o VÍNCULO (perfil, status,
// permissões); este mostra a PESSOA (bio, contatos, seguir).
import UserCardModal from "@/components/User/UserCardModal.vue";

// ── MANTIDO: ManagedAccountOnboarding ────────────────────────────────────────
import ManagedAccountOnboarding from "@/components/ManagedAccount/ManagedAccountOnboarding.vue";

// ── Modal de bulk remove ──────────────────────────────────────────────────────
import BulkRemoveMemberAccountForm from "./BulkRemoveMemberAccountForm.vue";

// Componente de busca
import SearchMemberAccountInput from "./SearchMemberAccountInput.vue";

// Paginação
import PaginationButtons from "../Generics/PaginationButtons.vue";
import PaginationInfo from "../Generics/PaginationInfo.vue";

// Aba Convites (conta existente entra no círculo só depois de aceitar)
import MemberInvitationsList from "./MemberInvitationsList.vue";
import { useRoute, useRouter } from "vue-router";
import * as managedAccountService from "@/services/managedAccountService";

// ── Stores ────────────────────────────────────────────────────────────────────
const managedAccountStore = useManagedAccountStore();
const userStore = useUserStore();
const { isManagedMember, canManageCircle } = useMemberRestrictions();

// ── Abas ──────────────────────────────────────────────────────────────────────
// A aba vive na URL (?tab=convites): é o destino do "Ver convites" do sino e
// do link do e-mail de resposta.
const route = useRoute();
const router = useRouter();
const activeTab = computed(() =>
  route.query.tab === "convites" ? "convites" : "membros"
);
const invitationsListRef = ref(null);
const pendingInvitesCount = ref(0);

function setTab(tab) {
  router.replace({
    query: { ...route.query, tab: tab === "convites" ? "convites" : undefined },
  });
}

// O contador da aba aparece antes de a pessoa abri-la.
async function loadPendingInvitesCount() {
  try {
    const { data } = await managedAccountService.fetchInvitations();
    pendingInvitesCount.value = data.filter((i) => i.status === "pending").length;
  } catch {
    // Contador é secundário; a aba carrega a lista de novo ao abrir.
  }
}

// ── Estados de exibição dos modais ───────────────────────────────────────────
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const showBatchDeleteModal = ref(false);
const showBulkAddModal = ref(false);
const showCardModal = ref(false);

// Card do usuário, aberto pelo avatar da linha.
const showUserCardModal = ref(false);
const selectedUserId = ref(null);

const showBulkRemoveFormModal = ref(false);

// ── Modal de permissões ───────────────────────────────────────────────────────
const showEditPermissionsModal = ref(false);
const selectedPermissionRelationshipId = ref(null);

const selectedRelationshipId = ref(null);

const selectedRelationship = computed(
  () => managedAccountStore.selectedRelationship
);

// ── Paginação ────────────────────────────────────────────────────────────────
const currentPage = ref(1);
const pageSize = ref(10);

// ✅ EVITA "FLASH" DE LISTA COMPLETA
const relationshipsToRender = ref([]);
const isTableReady = ref(false);
const isLoadingTable = ref(false);

// ── Dropdowns ────────────────────────────────────────────────────────────────
const openDropdownId = ref(null);
const isBatchDropdownOpen = ref(false);
const batchDropdownRef = ref(null);

// Força o SearchMemberAccountInput a limpar valor local/DOM quando necessário
const searchResetKey = ref(0);

function resetMemberAccountSearch() {
  if (typeof managedAccountStore.setSearchTerm === "function") {
    managedAccountStore.setSearchTerm("");
  } else {
    managedAccountStore.relationshipSearchTerm = "";
  }

  searchResetKey.value += 1;
}

// ── Dropdown de status por linha ─────────────────────────────────────────────
const openStatusDropdownId = ref(null);
const isStatusUpdating = ref(false);

// ── Toast ─────────────────────────────────────────────────────────────────────
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

// Conta nova entra na hora; conta existente vira convite e só aparece na
// tabela depois do aceite - a mensagem precisa dizer as duas coisas.
function batchDoneMessage(created, invited) {
  const parts = [];
  if (created > 0)
    parts.push(created === 1 ? "1 conta criada" : `${created} contas criadas`);
  if (invited > 0)
    parts.push(
      invited === 1 ? "1 convite enviado" : `${invited} convites enviados`
    );
  if (parts.length === 0) return "Contas membro adicionadas com sucesso!";
  let message = `${parts.join(" e ")}.`;
  if (invited > 0)
    message +=
      " Quem foi convidado entra no círculo ao aceitar; acompanhe na aba Convites.";
  return message.charAt(0).toUpperCase() + message.slice(1);
}

function handleChildNotify(payload) {
  if (!payload) return;

  if (payload.type === "success") {
    showSuccessToast(payload.message || "Operação realizada com sucesso.");
    return;
  }

  showErrorToast(payload.message || "Ocorreu um erro.");
}

// ── Permissões por linha ──────────────────────────────────────────────────────
// Editar (perfil e permissões) e revogar seguem a mesma regra: o titular sempre;
// o membro só com a delegação; e nenhum dos dois sobre o PRÓPRIO vínculo —
// revogar-se tiraria a pessoa do círculo, e editar-se seria escalada de
// privilégio. Espelha EnsureCanManageRelationshipAsync no backend.
function canEdit(rel) { return canDelete(rel); }
function canView() { return true; }
// Revogar: o titular revoga qualquer vínculo; o membro revoga se tiver a
// delegação, e aí vale para a lista inteira do círculo — mesma regra do backend
// (EnsureCanRevokeAsync).
function canDelete(rel) {
  // Vínculo revogado é terminal: o backend já rejeita editar permissões/perfil
  // (UpdatePermissionsAsync/UpdateRelationshipTypeAsync) e revogar de novo
  // (RevokeAsync) com esse status. Travar aqui evita abrir o modal só para a
  // pessoa descobrir isso depois de configurar tudo e tentar salvar.
  if (rel?.status === "Revoked") return false;

  if (!isManagedMember.value) return true;
  if (!canManageCircle.value) return false;

  // Nem o delegado revoga a si mesmo: sairia do círculo na mesma ação e
  // perderia o acesso ao contexto (espelha EnsureCanRevokeAsync).
  return Number(rel?.managedUserId) !== Number(userStore.currentUser?.userId);
}

// Só a própria linha fica travada para o delegado; explica o porquê.
function rowActionHint(rel) {
  if (rel?.status === "Revoked") {
    return "Vínculo já revogado — não é possível editar nem revogar de novo.";
  }
  if (canDelete(rel)) return null;
  return "Você não pode alterar nem revogar o seu próprio vínculo. Peça ao titular.";
}

// ── Seleção de página ─────────────────────────────────────────────────────────
const isAllSelectedOnPage = computed({
  get: () => {
    const editableIdsOnPage = relationshipsToRender.value
      .filter((r) => canDelete(r))
      .map((r) => r.managedAccountRelationshipId);

    if (editableIdsOnPage.length === 0) return false;

    return editableIdsOnPage.every((id) =>
      managedAccountStore.selectedRelationshipIds.includes(id)
    );
  },
  set: (value) => {
    const editableIdsOnPage = relationshipsToRender.value
      .filter((r) => canDelete(r))
      .map((r) => r.managedAccountRelationshipId);

    const allIdsOnPage = relationshipsToRender.value.map(
      (r) => r.managedAccountRelationshipId
    );

    if (value) {
      editableIdsOnPage.forEach((id) => {
        if (!managedAccountStore.selectedRelationshipIds.includes(id)) {
          managedAccountStore.selectedRelationshipIds.push(id);
        }
      });
    } else {
      managedAccountStore.selectedRelationshipIds =
        managedAccountStore.selectedRelationshipIds.filter(
          (id) => !allIdsOnPage.includes(id)
        );
    }
  },
});

const anyEditableOnPage = computed(() =>
  relationshipsToRender.value.some((r) => canDelete(r))
);

const canBulkRevoke = computed(() => {
  return (managedAccountStore.totalActiveCount ?? 0) > 0;
});

const canRevokeSelected = computed(() => {
  if (!Array.isArray(managedAccountStore.selectedRelationshipIds)) return false;
  return managedAccountStore.selectedRelationshipIds.length > 0;
});

// ── Helpers de exibição ───────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: "Active", label: "Ativo" },
  { value: "Inactive", label: "Inativo" },
  { value: "Revoked", label: "Revogado" },
];

function statusLabel(status) {
  const map = { Active: "Ativo", Inactive: "Inativo", Revoked: "Revogado" };
  return map[status] ?? status ?? "—";
}

function statusClass(status) {
  if (status === "Active") return "status-badge status-active";
  if (status === "Inactive") return "status-badge status-inactive";
  if (status === "Revoked") return "status-badge status-revoked";
  return "status-badge";
}

function relationshipTypeLabel(type) {
  if (!type || type.trim() === "") return "—";
  return type;
}

function normalizeAvatarPath(path) {
  if (!path) return "";

  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  const base = api.defaults.baseURL.replace(/\/api$/, "").replace(/\/+$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;

  return `${base}${cleanPath}`;
}

function getAvatarUrl(photoPath) {
  if (!photoPath) return "";
  return normalizeAvatarPath(photoPath);
}

function resolveMemberPhotoPath(rel) {
  if (!rel) return null;

  return (
    rel.photoPath ||
    rel.photoUrl ||
    rel.userPhotoPath ||
    rel.userPhotoUrl ||
    rel.managedUserPhotoPath ||
    rel.managedUserPhotoUrl ||
    rel.managedUser?.photoPath ||
    rel.managedUser?.photoUrl ||
    rel.photo_path ||
    rel.user_photo_path ||
    rel.managed_user_photo_path ||
    null
  );
}

// ── Busca e fetch ─────────────────────────────────────────────────────────────
async function fetchRelationships() {
  try {
    isLoadingTable.value = true;

    if (!isTableReady.value) relationshipsToRender.value = [];

    await managedAccountStore.fetchPaged(
      currentPage.value,
      pageSize.value,
      managedAccountStore.relationshipSearchTerm
    );

    relationshipsToRender.value = Array.isArray(
      managedAccountStore.pagedRelationships
    )
      ? [...managedAccountStore.pagedRelationships]
      : [];

    isTableReady.value = true;

    if (managedAccountStore.shouldForcePageOne) {
      managedAccountStore.shouldForcePageOne = false;

      if (currentPage.value !== 1) {
        currentPage.value = 1;
      } else {
        await managedAccountStore.fetchPaged(
          1,
          pageSize.value,
          managedAccountStore.relationshipSearchTerm
        );

        relationshipsToRender.value = Array.isArray(
          managedAccountStore.pagedRelationships
        )
          ? [...managedAccountStore.pagedRelationships]
          : [];
      }
    }
  } catch (error) {
    console.error("❌ Erro ao buscar vínculos:", error?.message);
    showErrorToast("Erro ao carregar contas membro.");
  } finally {
    isLoadingTable.value = false;
  }
}

watch(currentPage, fetchRelationships);

watch(
  () => managedAccountStore.relationshipSearchTerm,
  () => {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
    } else {
      fetchRelationships();
    }
  }
);

watch(
  () => managedAccountStore.pagedRelationships,
  (val) => {
    if (!isTableReady.value) return;
    relationshipsToRender.value = Array.isArray(val) ? [...val] : [];
  }
);

// ── Handler pós-mudança ───────────────────────────────────────────────────────
async function handleRelationshipsChanged(options = {}) {
  const {
    goToFirstPage = true,
    closeEdit = true,
    closeDelete = true,
    closeBatchDelete = true,
    closeBulkAdd = true,
    clearSelection = false,
    resetSearch = false,
    successMessage = null,
  } = options;

  // O lote pode ter gerado convites: a aba e o contador se atualizam junto.
  if (invitationsListRef.value) invitationsListRef.value.load();
  else loadPendingInvitesCount();

  if (resetSearch) resetMemberAccountSearch();

  if (closeEdit) showEditModal.value = false;
  if (closeDelete) showDeleteModal.value = false;
  if (closeBatchDelete) showBatchDeleteModal.value = false;
  if (closeBulkAdd) showBulkAddModal.value = false;

  if (clearSelection) managedAccountStore.clearSelection();
  if (successMessage) showSuccessToast(successMessage);

  if (goToFirstPage) {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
      return;
    }
    await fetchRelationships();
    return;
  }

  await fetchRelationships();
}

// Quem acabou de convidar vai direto para a aba Convites, onde o convite aparece.
async function handleBulkAddDone({ skipped, created = 0, invited = 0 } = {}) {
  if (!skipped && invited > 0) setTab("convites");
  await handleRelationshipsChanged({
    goToFirstPage: true,
    closeBulkAdd: true,
    clearSelection: true,
    resetSearch: true,
    successMessage: skipped ? null : batchDoneMessage(created, invited),
  });
}

async function handleRelationshipDeleted() {
  await handleRelationshipsChanged({
    goToFirstPage: false,
    closeDelete: true,
    clearSelection: false,
  });
}

async function handleBatchRelationshipsDeleted() {
  await handleRelationshipsChanged({
    goToFirstPage: false,
    closeBatchDelete: true,
    clearSelection: true,
  });
}

async function handleCardRelationshipUpdated() {
  await handleRelationshipsChanged({
    goToFirstPage: false,
    closeEdit: false,
    closeDelete: false,
    closeBatchDelete: false,
    closeBulkAdd: false,
    clearSelection: false,
  });
}

// ── Dropdown por linha ────────────────────────────────────────────────────────
function toggleRowDropdown(relationshipId) {
  openDropdownId.value =
    openDropdownId.value === relationshipId ? null : relationshipId;
  openStatusDropdownId.value = null;
}

// ── Alternar status ───────────────────────────────────────────────────────────
function toggleStatusDropdown(relationshipId, event) {
  event.stopPropagation();
  openStatusDropdownId.value =
    openStatusDropdownId.value === relationshipId ? null : relationshipId;
  openDropdownId.value = null;
}

async function handleStatusChange(relationship, newStatus) {
  if (relationship.status === newStatus) {
    openStatusDropdownId.value = null;
    return;
  }
  if (relationship.status === "Revoked" && newStatus !== "Revoked") {
    showErrorToast("Um vínculo revogado não pode ser reativado.");
    openStatusDropdownId.value = null;
    return;
  }

  isStatusUpdating.value = true;
  openStatusDropdownId.value = null;
  try {
    await managedAccountStore.updateStatus(
      relationship.managedAccountRelationshipId,
      newStatus
    );
    showSuccessToast(
      `Status alterado para "${statusLabel(newStatus)}" com sucesso.`
    );
    await fetchRelationships();
  } catch (e) {
    showErrorToast(e.message || "Erro ao alterar status.");
  } finally {
    isStatusUpdating.value = false;
  }
}

// ── Ações por linha ───────────────────────────────────────────────────────────
function indexSelector(index) {
  const rel = relationshipsToRender.value[index];
  if (!rel) return;
  managedAccountStore.setSelectedRelationship(rel);
}

function handleEdit(index) {
  const rel = relationshipsToRender.value[index];
  indexSelector(index);
  selectedPermissionRelationshipId.value = rel.managedAccountRelationshipId;
  showEditPermissionsModal.value = true;
  openDropdownId.value = null;
}

function handleView(index) {
  handleOpenCard(index);
  openDropdownId.value = null;
}

// Card do usuário pelo avatar. A linha inteira já abre o card do vínculo
// (handleOpenCard), então quem chama isto precisa de @click.stop — senão os
// dois modais abrem empilhados.
function handleOpenUserCard(rel) {
  const userId = Number(rel?.managedUserId);
  if (!userId) return;
  selectedUserId.value = userId;
  showUserCardModal.value = true;
}

function handleOpenCard(index) {
  const rel = relationshipsToRender.value[index];
  if (!rel) return;
  selectedRelationshipId.value = rel.managedAccountRelationshipId;
  managedAccountStore.setSelectedRelationship(rel);
  showCardModal.value = true;
}

function handleDelete(index) {
  indexSelector(index);
  showDeleteModal.value = true;
  openDropdownId.value = null;
}

function toggleSelection(id) {
  managedAccountStore.toggleRelationshipSelection(id);
}

// ── Ações em lote ─────────────────────────────────────────────────────────────
function tryOpenBatchDeleteModal() {
  isBatchDropdownOpen.value = false;
  if (
    !Array.isArray(managedAccountStore.selectedRelationshipIds) ||
    managedAccountStore.selectedRelationshipIds.length === 0
  ) {
    showErrorToast("Nenhum vínculo selecionado.");
    return;
  }
  showBatchDeleteModal.value = true;
}

// ── Fechar dropdowns ao clicar fora ──────────────────────────────────────────
function handleClickOutside(event) {
  if (
    batchDropdownRef.value &&
    !batchDropdownRef.value.contains(event.target)
  ) {
    isBatchDropdownOpen.value = false;
  }
  if (openDropdownId.value && !event.target.closest(".action-cell-container")) {
    openDropdownId.value = null;
  }
  if (
    openStatusDropdownId.value &&
    !event.target.closest(".status-cell-container")
  ) {
    openStatusDropdownId.value = null;
  }
}

// ── Ciclo de vida ─────────────────────────────────────────────────────────────
onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  fetchRelationships();
  if (activeTab.value === "membros") loadPendingInvitesCount();
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);

  managedAccountStore.relationshipSearchTerm = "";
  managedAccountStore.currentPage = 1;
  managedAccountStore.shouldForcePageOne = true;
  managedAccountStore.selectedRelationshipIds = [];
  managedAccountStore.selectedRelationship = null;
  openDropdownId.value = null;
  openStatusDropdownId.value = null;
  isBatchDropdownOpen.value = false;
  selectedRelationshipId.value = null;
  showEditModal.value = false;
  showDeleteModal.value = false;
  showBatchDeleteModal.value = false;
  showBulkAddModal.value = false;
  showBulkRemoveFormModal.value = false;
  showEditPermissionsModal.value = false;
  selectedPermissionRelationshipId.value = null;
  showCardModal.value = false;
  showUserCardModal.value = false;
  selectedUserId.value = null;

  relationshipsToRender.value = [];
  isTableReady.value = false;
  isLoadingTable.value = false;
});
</script>

<template>
  <div class="w-full h-full">
    <div class="main-container">
      <!-- ── Cabeçalho ────────────────────────────────────────────────────── -->
      <div class="main-header">
        <div class="flex flex-col gap-0">
          <div class="text-xl font-bold">Contas Membro</div>
          <div class="text-sm mt-1 text-gray-700 ">
            Gerencie as contas vinculadas à sua conta titular.
          </div>
        </div>
      </div>

      <!-- ── Abas: Membros | Convites ───────────────────────────────────────── -->
      <div class="member-tabs" role="tablist" aria-label="Contas membro">
        <button
          type="button"
          role="tab"
          :aria-selected="activeTab === 'membros'"
          :class="['member-tab', activeTab === 'membros' && 'member-tab--active']"
          @click="setTab('membros')"
        >
          Membros
        </button>
        <button
          type="button"
          role="tab"
          :aria-selected="activeTab === 'convites'"
          :class="['member-tab', activeTab === 'convites' && 'member-tab--active']"
          @click="setTab('convites')"
        >
          Convites
          <span v-if="pendingInvitesCount" class="member-tab-count">{{
            pendingInvitesCount
          }}</span>
        </button>
      </div>

      <template v-if="activeTab === 'convites'">
        <div class="action-bar">
          <p class="invites-intro">
            Quem já tem conta entra no seu círculo por convite, e só depois de
            aceitar. Convite pendente não ocupa vaga do plano.
          </p>
          <button @click="showBulkAddModal = true" class="btn btn-primary">
            <i class="fas fa-user-plus" aria-hidden="true"></i>
            Adicionar Conta Membro
          </button>
        </div>
        <MemberInvitationsList
          ref="invitationsListRef"
          @notify="handleChildNotify"
          @count="(n) => (pendingInvitesCount = n)"
        />
      </template>

      <template v-else>
      <!-- ── Barra de ações ────────────────────────────────────────────────── -->
      <div class="action-bar">
        <div class="w-full md:w-1/2">
          <SearchMemberAccountInput :reset-key="searchResetKey" />
        </div>

        <div
          class="w-full md:w-auto flex flex-col md:flex-row space-y-2 md:space-y-0 items-stretch md:items-center justify-end md:space-x-3 flex-shrink-0"
        >
          <button @click="showBulkAddModal = true" class="btn btn-primary">
            <svg class="h-4 w-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path
                clip-rule="evenodd"
                fill-rule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              />
            </svg>
            Adicionar Conta Membro
          </button>

          <div ref="batchDropdownRef" class="relative">
            <button
              @click="isBatchDropdownOpen = !isBatchDropdownOpen"
              class="btn btn-batch w-full"
            >
              Ações em lote
              <svg
                class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                :class="{ open: isBatchDropdownOpen }"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  clip-rule="evenodd"
                  fill-rule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                />
              </svg>
            </button>

            <div v-if="isBatchDropdownOpen" class="dropdown-menu">
              <div class="py-1">
                <a
                  v-if="canBulkRevoke"
                  href="#"
                  role="button"
                  @click.prevent="
                    isBatchDropdownOpen = false;
                    showBulkRemoveFormModal = true;
                  "
                  class="dropdown-item danger-item"
                >
                  <i class="fas fa-minus-circle"></i>
                  <span>Apagar/Revogar em Lote</span>
                </a>

                <div class="border-t border-gray-200 my-1"></div>

                <a
                  href="#"
                  role="button"
                  @click.prevent="
                    canRevokeSelected && tryOpenBatchDeleteModal()
                  "
                  class="dropdown-item danger-item"
                  :class="{ 'disabled-item': !canRevokeSelected }"
                >
                  <i class="fas fa-trash-alt"></i>
                  <span>Apagar/Revogar Selecionados</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Tabela ────────────────────────────────────────────────────────── -->
      <div class="table-wrapper">
        <table class="main-table">
          <thead>
            <tr>
              <th style="width: 4%">
                <input
                  type="checkbox"
                  v-model="isAllSelectedOnPage"
                  class="checkbox-style"
                  :disabled="!anyEditableOnPage"
                  :class="{ 'disabled-item': !anyEditableOnPage }"
                />
              </th>
              <th style="width: 80px">Ações</th>
              <th style="width: 7%">ID</th>
              <th style="width: 13%">Nome Completo</th>
              <th style="width: 10%">Username</th>
              <th style="width: 16%">E-mail</th>
              <th style="width: 10%">Status</th>
              <th style="width: 12%">Perfil</th>
              <th style="width: 11%">Permissões</th>
              <th style="width: 13%">Criado em</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(rel, index) in !isLoadingTable
                ? relationshipsToRender
                : []"
              :key="rel.managedAccountRelationshipId"
              @click="handleOpenCard(index)"
              class="clickable-row"
            >
              <!-- O título fica no invólucro, não no input: elemento disabled
                   não dispara evento de mouse, então o tooltip do próprio
                   checkbox nunca apareceria. -->
              <td @click.stop :title="rowActionHint(rel)">
                <input
                  type="checkbox"
                  :class="{ 'checkbox-disabled': !canDelete(rel) }"
                  :value="rel.managedAccountRelationshipId"
                  :checked="
                    managedAccountStore.selectedRelationshipIds.includes(
                      rel.managedAccountRelationshipId
                    )
                  "
                  @change="toggleSelection(rel.managedAccountRelationshipId)"
                  class="checkbox-style"
                  :disabled="!canDelete(rel)"

                />
              </td>

              <td @click.stop class="action-cell-container">
                <div class="row-actions">
                  <button
                    @click="toggleRowDropdown(rel.managedAccountRelationshipId)"
                    class="btn-icon btn-soft-brand"
                    title="Menu de Ações"
                  >
                    <i class="fas fa-ellipsis-h fa-lg"></i>
                  </button>
                </div>

                <div
                  v-if="openDropdownId === rel.managedAccountRelationshipId"
                  class="row-dropdown-menu"
                >
                  <ul>
                    <li
                      :class="{ 'disabled-item': !canEdit(rel) }"
                      :title="
                        canEdit(rel)
                          ? null
                          : rowActionHint(rel)
                      "
                      @click="canEdit(rel) && handleEdit(index)"
                    >
                      <i class="fas fa-pencil-alt"></i><span>Editar</span>
                    </li>
                    <li
                      :class="{ 'disabled-item': !canView(rel) }"
                      @click="canView(rel) && handleView(index)"
                    >
                      <i class="fas fa-eye"></i><span>Visualizar</span>
                    </li>
                    <li
                      class="danger-item"
                      :class="{ 'disabled-item': !canDelete(rel) }"
                      :title="canDelete(rel) ? null : rowActionHint(rel)"
                      @click="canDelete(rel) && handleDelete(index)"
                    >
                      <i class="fas fa-ban"></i>
                      <span>Apagar/Revogar</span>
                    </li>
                    <li
                      class="danger-item disabled-item"
                      title="Delete físico não disponível nesta versão"
                    >
                      <i class="fas fa-trash-alt"></i>
                      <span>Excluir definitivamente</span>
                    </li>
                  </ul>
                </div>
              </td>

              <td>
                <span>{{ rel.managedAccountRelationshipId }}</span>
              </td>

              <td class="cell-text-center">
                <span class="member-name-cell">
                  <!-- @click.stop: a linha inteira abre o card do VÍNCULO; o
                       avatar abre o card da PESSOA. Sem o stop, os dois. -->
                  <button
                    type="button"
                    class="member-avatar member-avatar--button"
                    :title="`Ver o perfil de ${
                      toTitleCase(rel.fullName) || rel.userName
                    }`"
                    @click.stop="handleOpenUserCard(rel)"
                  >
                    <span
                      v-if="getAvatarUrl(resolveMemberPhotoPath(rel))"
                      class="member-avatar-img"
                      :style="{
                        backgroundImage: `url('${getAvatarUrl(
                          resolveMemberPhotoPath(rel)
                        )}')`,
                      }"
                    ></span>

                    <i v-else class="fas fa-user member-avatar-icon"></i>
                  </button>

                  <span class="member-name-text">
                    {{ toTitleCase(rel.fullName) || "—" }}
                  </span>
                </span>
              </td>

              <td class="cell-text-center">
                <span>@{{ rel.userName }}</span>
              </td>

              <td class="cell-text-center">
                <span>{{ rel.email }}</span>
              </td>

              <td @click.stop class="status-cell-container">
                <button
                  :class="statusClass(rel.status)"
                  @click="
                    toggleStatusDropdown(
                      rel.managedAccountRelationshipId,
                      $event
                    )
                  "
                  :disabled="
                    isStatusUpdating ||
                    rel.status === 'Revoked' ||
                    !canDelete(rel)
                  "
                  :title="
                    rel.status === 'Revoked'
                      ? 'Vínculo revogado não pode ser alterado'
                      : rowActionHint(rel) || 'Clique para alterar o status'
                  "
                >
                  {{ statusLabel(rel.status) }}
                  <i
                    v-if="rel.status !== 'Revoked'"
                    class="fas fa-chevron-down status-chevron"
                  ></i>
                </button>

                <div
                  v-if="
                    openStatusDropdownId === rel.managedAccountRelationshipId
                  "
                  class="status-dropdown-menu"
                >
                  <ul>
                    <li
                      v-for="opt in STATUS_OPTIONS"
                      :key="opt.value"
                      :class="[
 'status-option',
                        rel.status === opt.value
                          ? 'status-option--current'
                          : '',
                      ]"
                      @click="handleStatusChange(rel, opt.value)"
                    >
                      {{ opt.label }}
                      <i
                        v-if="rel.status === opt.value"
                        class="fas fa-check status-check-icon"
                      ></i>
                    </li>
                  </ul>
                </div>
              </td>

              <td class="cell-type-badge">
                <span class="type-badge">
                  {{ relationshipTypeLabel(rel.relationshipType) }}
                </span>
              </td>

              <!-- Sem `cell-text-center`: apesar do nome, essa classe alinha à
                   ESQUERDA, e é o que as colunas de texto (nome, username,
                   e-mail) querem. Aqui o conteúdo é uma chip, que fica centrada
                   herdando o `text-align` da tabela — mesmo caminho da coluna
                   Status. -->
              <td @click.stop>
                <button
                  class="permissions-badge"
                  :class="
 rel.permissions
                      ? 'permissions-badge--custom'
                      : 'permissions-badge--default'
                  "
                  :disabled="!canEdit(rel)"
                  @click="handleEdit(index)"
                  :title="
                    canEdit(rel)
                      ? rel.permissions
                        ? 'Permissões personalizadas — clique para editar'
                        : 'Usando permissões padrão — clique para personalizar'
                      : rowActionHint(rel)
                  "
                >
                  <i
                    :class="
 rel.permissions ? 'fas fa-lock' : 'fas fa-lock-open'
                    "
                    class="permissions-icon"
                  ></i>
                  {{ rel.permissions ? "Personalizado" : "Padrão" }}
                </button>
              </td>

              <td>
                <span>{{ formatDateTimeForDisplay(rel.createdAt) }}</span>
              </td>
            </tr>

            <tr v-if="isLoadingTable" class="empty-row-state">
              <td colspan="10" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <svg class="empty-spinner" viewBox="0 0 24 24" fill="none">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  <p>Carregando contas membro...</p>
                </div>
              </td>
            </tr>

            <tr
              v-else-if="relationshipsToRender.length === 0"
              class="empty-row-state"
            >
              <td colspan="10" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <img src="/img/lupa.webp" alt="" width="38" height="40" class="empty-icon-img" />
                  <p>Nenhuma conta membro encontrada.</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- ── Paginação ──────────────────────────────────────────────────── -->
        <div
          v-if="!isLoadingTable && (managedAccountStore.totalCount ?? 0) > 0"
          class="usergroup-pagination-row"
        >
          <div class="usergroup-pagination-left">
            <span class="usergroup-pagination-label">Mostrando</span>
            <PaginationInfo
              :current-page="currentPage"
              :page-size="pageSize"
              :total-count="managedAccountStore.totalCount ?? 0"
            />
            <span>vínculos</span>
          </div>

          <PaginationButtons
            :current-page="currentPage"
            :page-size="pageSize"
            :total-count="managedAccountStore.totalCount ?? 0"
            :total-pages="managedAccountStore.totalPages"
            @change-page="(page) => (currentPage = page)"
          />
        </div>
      </div>
      </template>
    </div>
  </div>

  <!-- ── Modais (Teleport para body) ──────────────────────────────────────── -->
  <Teleport to="body">
    <MemberAccountCardModal
      v-if="showCardModal"
      :show="showCardModal"
      :relationship-id="selectedRelationshipId"
      @close="showCardModal = false"
      @relationship-updated="handleCardRelationshipUpdated"
      @notify="handleChildNotify"
    />

    <!-- v-if, e não só :show: o UserCardModal busca os dados em onMounted, sem
         watch no userId. Mantido montado, ele mostraria a pessoa da linha
         anterior ao abrir a seguinte. -->
    <UserCardModal
      v-if="showUserCardModal && selectedUserId"
      :show="showUserCardModal"
      :userId="selectedUserId"
      @close="showUserCardModal = false"
    />

    <BulkRemoveMemberAccountForm
      v-if="showBulkRemoveFormModal"
      @close="showBulkRemoveFormModal = false"
      @done="
        handleRelationshipsChanged({
          goToFirstPage: true,
          clearSelection: true,
          successMessage: 'Vínculos revogados com sucesso!',
        });
        showBulkRemoveFormModal = false;
      "
    />

    <DeleteMemberAccountForm
      v-if="showDeleteModal && selectedRelationship"
      :relationship="selectedRelationship"
      @close="showDeleteModal = false"
      @refresh="handleRelationshipDeleted"
      @notify="handleChildNotify"
    />

    <BatchDeleteMemberAccountForm
      v-if="showBatchDeleteModal"
      @close="showBatchDeleteModal = false"
      @refresh="handleBatchRelationshipsDeleted"
      @notify="handleChildNotify"
    />

    <ManagedAccountOnboarding
      v-if="showBulkAddModal"
      @done="handleBulkAddDone"
    />

    <EditMemberAccountForm
      v-if="showEditPermissionsModal"
      :relationship-id="selectedPermissionRelationshipId"
      @close="showEditPermissionsModal = false"
      @saved="
        handleRelationshipsChanged({ goToFirstPage: false });
        showEditPermissionsModal = false;
      "
    />
  </Teleport>

  <!-- ── Toast de notificação ──────────────────────────────────────────────── -->
  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            &times;
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
/* ── Abas Membros | Convites ─────────────────────────────────────────────── */
/* Chips como as abas do Perfil (AccountSettingsModal): a ativa ganha fundo. */
.member-tabs {
  display: flex;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  border-bottom: 1px solid var(--border-color);
}
.member-tab {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: 0.4rem 0.8rem;
  border: none;
  border-radius: var(--radius-pill);
  background-color: transparent;
  color: var(--text-dark);
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: var(--font-medium);
  cursor: pointer;
  transition: background-color var(--transition-base), transform var(--transition-press);
}
.member-tab:hover {
  background-color: var(--brand-tint);
}
.member-tab:active {
  transform: translateY(1px);
}
.member-tab:focus,
.member-tab:focus-visible {
  outline: none;
  box-shadow: none;
}
.member-tab--active,
.member-tab--active:hover {
  background-color: var(--brand-soft);
  color: var(--text-on-brand-soft);
}
/* O contador usa o mesmo fundo da chip ativa; nela, inverte para aparecer. */
.member-tab--active .member-tab-count {
  background: var(--surface);
}
.member-tab-count {
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: var(--radius-pill);
  background: var(--brand-soft);
  color: var(--text-on-brand-soft);
  font-size: 0.7rem;
  line-height: 18px;
  text-align: center;
}
.invites-intro {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--text-subtle);
}

@media (min-width: 768px) {
  .action-bar {
    flex-direction: row;
    align-items: center;
  }
}

.row-dropdown-menu {
  min-width: 185px;
}

/* ── Status: badge + dropdown por linha ─────────────────────────────────── */
.status-cell-container {
  position: relative;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: filter 0.15s, transform 0.05s ease;
}
.status-badge:hover:not(:disabled) {
  filter: brightness(0.93);
}
.status-badge:active:not(:disabled) {
  transform: translateY(1px);
}
.status-badge:focus,
.status-badge:focus-visible {
  outline: none;
  box-shadow: none;
}
.status-badge:disabled {
  cursor: not-allowed;
}
.status-badge:disabled:hover {
  transform: none;
  box-shadow: none;
}
.status-active  { background: var(--success-bg); color: var(--success-text); }
.status-inactive { background: var(--warning-bg); color: #854d0e; }
.status-revoked  { background: var(--danger-bg); color: var(--danger-text); }

.status-chevron {
  font-size: 0.6rem;
  opacity: 0.7;
}

.status-dropdown-menu {
  position: absolute;
  left: 0;
  top: calc(100% + 4px);
  z-index: 100;
  background: var(--surface);
  border: 1px solid var(--neutral-200);
  border-radius: 10px;
  box-shadow: var(--shadow-dropdown);
  min-width: 140px;
}
.status-dropdown-menu ul {
  list-style: none;
  padding: 0.3rem;
  margin: 0;
}
.status-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.45rem 0.7rem;
  border-radius: 7px;
  font-size: 0.82rem;
  color: var(--text-dark);
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}
.status-option:hover { background: var(--surface-3); }
.status-option:active {
  transform: translateY(1px);
}
.status-option:focus,
.status-option:focus-visible {
  outline: none;
  box-shadow: none;
}
.status-option--current {
  font-weight: 700;
  color: var(--text-dark);
  background: #f0eeff;
}
.status-check-icon {
  color: var(--brand-light);
  font-size: 0.75rem;
}

/* ── Perfil ────────────────────────────────────────────────────── */
.type-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.2rem 0.4rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: capitalize;
  box-sizing: border-box;
  background-color: var(--brand-soft);
  color: var(--text-on-brand-soft);
}

/* ── Permissões: badge de estado ────────────────────────────────────────── */
.permissions-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: filter 0.15s, transform 0.05s ease;
  white-space: nowrap;
}
.permissions-badge:hover {
  filter: brightness(0.93);
}
.permissions-badge:active {
  transform: translateY(1px);
}
.permissions-badge:focus,
.permissions-badge:focus-visible {
  outline: none;
  box-shadow: none;
}
.permissions-badge--default { background: var(--neutral-200); color: var(--text-light); }
.permissions-badge--custom  { background: var(--brand-soft); color: var(--text-on-brand-soft); }
.permissions-badge:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.permissions-badge:disabled:hover {
  filter: none;
}
.permissions-icon { font-size: 0.65rem; }

.cell-text-center { text-align: left; }
.cell-type-badge {
  text-align: center;
  padding: 0.3rem 0.8rem;
  vertical-align: middle;
}

/* ── Paginação ──────────────────────────────────────────────────────────── */
.usergroup-pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem 1rem;
  font-size: 0.82rem;
}
.usergroup-pagination-left {
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.usergroup-pagination-label {
  padding-top: 2px;
}

.empty-state-content {
  width: 100%;
  margin: 0 auto;
}

/* ── Avatar na coluna Nome Completo ─────────────────────────────────────── */
.member-name-cell {
  display: inline-flex !important;
  align-items: center;
  justify-content: flex-start;
  gap: 0.45rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

/* A regra genérica `.main-table td > span` aplica overflow: hidden a TODO span
   filho direto de célula, para dar reticência no texto. Nesta célula, porém, o
   span é o invólucro do avatar + nome: ele tem exatamente a altura do avatar
   (24px) e começa colado nele, então recortava o anel de hover, que é desenhado
   2px PARA FORA da caixa — o círculo saía achatado em cima, embaixo e nas
   laterais.
   Liberar aqui não perde a reticência: quem trunca é o .member-name-text, que
   tem overflow/ellipsis próprios.
   O seletor precisa repetir `.main-table td > span` porque a regra genérica
   (0,1,2) vence `.member-name-cell` sozinha (0,1,0). */
.main-table td > span.member-name-cell {
  overflow: visible;
}
.member-avatar {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background-color: var(--border-color);
  color: var(--text-muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex: 0 0 24px;
}
/* O avatar virou <button> para abrir o card do usuário: zera o cromo do botão
   (o resto do visual continua vindo de .member-avatar) e dá o sinal de que é
   clicável, já que a linha inteira também é. */
.member-avatar--button {
  padding: 0;
  border: none;
  cursor: pointer;
  transition: box-shadow 0.15s, transform 0.1s;
}
.member-avatar--button:hover {
  box-shadow: 0 0 0 2px var(--brand-light);
}
.member-avatar--button:active {
  transform: scale(0.94);
}
.member-avatar--button:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--brand-hover);
}
.member-avatar-img {
  width: 100%;
  height: 100%;
  display: block;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}
.member-avatar-icon {
  font-size: 0.72rem;
  line-height: 1;
}
.member-name-text {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>